import React, { useState, useEffect, useMemo } from 'react';
import {
  FiMail, FiEdit3, FiSave, FiChevronLeft, FiLoader, FiSend,
  FiSmartphone, FiMonitor,
} from 'react-icons/fi';
import { supabase } from '@/integrations/supabase/client';

/**
 * Admin — Email Templates panel (V3 refonte).
 *
 * Two-column layout on desktop:
 *   Left  : list of the 10 canonical template_keys with status pill.
 *   Right : editor + real preview.
 *
 * Preview mode:
 *   ▸ Desktop (580px card centered) — matches wrapEmailHtml() output.
 *   ▸ Mobile (375px card) — same content, mobile viewport constraint.
 *   ▸ Real HTML preview via <iframe srcdoc> so template CSS never
 *     leaks into the admin dashboard.
 *
 * Variables are extracted from the body via regex so the editor shows
 * the caller which {{tokens}} exist, and a "Test send" button ships the
 * template to any address via send-broadcast-email (as testEmail).
 */

interface Template {
  id?: string;
  template_key: string;
  subject: string;
  description: string | null;
  html_content: string;
  updated_at?: string;
}

interface AnalyticsRow {
  template_key: string;
  sent: number;
  delivered: number;
  opened: number;
  clicked: number;
  bounced: number;
  complained: number;
  unsubscribed: number;
  open_rate: number;
  click_rate: number;
  last_event_at: string | null;
}

const fmtPct = (r: number) => `${Math.round(r * 100)}%`;

// Sample values used by the preview so tokens render like real emails.
const SAMPLE_VARS: Record<string, string> = {
  name:            'Alex',
  email:           'alex@example.com',
  amount:          '$49.00',
  plan:            'Pro Trader',
  billing_type:    'Monthly',
  period_end:      'October 22, 2026',
  previous_plan:   'Starter',
  days_left:       '3',
  trial_days:      '7',
  trial_end_date:  'October 22, 2026',
  invoice_id:      'INV-2026-000042',
  reset_link:      'https://snap-chart.ai/reset?token=demo',
  redeem_url:      'https://snap-chart.ai/login?trial=demo',
  app_url:         'https://snap-chart.ai',
};

// JS mirror of supabase/functions/_shared/emailTemplate.ts wrapEmailHtml()
// so previewed HTML matches the sent email byte-for-byte. Keep in sync.
const buildPreviewHtml = (bodyHtml: string) => `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="margin:0;padding:0;font-family:-apple-system,BlinkMacSystemFont,'SF Pro Display','Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;background-color:#f4f5f7;-webkit-font-smoothing:antialiased;">
  <div style="max-width:580px;margin:0 auto;padding:40px 20px;">
    <div style="text-align:left;margin:0 0 28px 0;padding:0 4px;">
      <span style="font-size:20px;font-weight:800;color:#0a0e1a;letter-spacing:-0.02em;">Snap Chart AI</span>
      <span style="display:inline-block;margin-left:8px;font-family:ui-monospace,'SF Mono',Menlo,Consolas,monospace;font-size:10px;text-transform:uppercase;letter-spacing:0.25em;color:#0891b2;vertical-align:middle;">▸ ai risk officer</span>
    </div>
    <div style="background:#ffffff;border:1px solid #e5e7eb;border-radius:16px;padding:36px;box-shadow:0 1px 3px rgba(15,23,42,0.06),0 8px 24px rgba(15,23,42,0.04);">
      ${bodyHtml}
    </div>
    <div style="margin:36px 4px 0 4px;padding:20px 0 0 0;border-top:1px solid #e5e7eb;">
      <p style="color:#64748b;font-size:12px;line-height:1.6;margin:0;">
        <strong style="color:#334155;">Snap Chart AI</strong><br>
        Ocean Reef, Panamá
      </p>
      <p style="color:#94a3b8;font-size:12px;margin:14px 0 0 0;">
        <a href="https://snap-chart.ai/privacy" style="color:#64748b;text-decoration:none;">Privacy</a> ·
        <a href="https://snap-chart.ai/terms" style="color:#64748b;text-decoration:none;">Terms</a> ·
        <a href="mailto:support@snap-chart.ai" style="color:#64748b;text-decoration:none;">Support</a>
      </p>
    </div>
  </div>
</body>
</html>`;

const substitute = (s: string, vars: Record<string, string>) =>
  s.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k) => vars[k] ?? `{{${k}}}`);

const StatusPill: React.FC<{ ok: boolean }> = ({ ok }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border font-mono text-[10px] uppercase tracking-widest ${
    ok
      ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
      : 'bg-red-500/10 border-red-500/30 text-red-400'
  }`}>
    <span className={`w-1.5 h-1.5 rounded-full ${ok ? 'bg-emerald-400' : 'bg-red-400'}`} />
    {ok ? 'Active' : 'Missing'}
  </span>
);

const EmailTemplatesPanel: React.FC = () => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [analytics, setAnalytics] = useState<Record<string, AnalyticsRow>>({});
  const [loading, setLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Template | null>(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop');
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: tpls }, { data: analyticsRes }] = await Promise.all([
        supabase.from('email_templates').select('*').order('template_key'),
        supabase.rpc('get_email_template_analytics', { p_days: 30 }),
      ]);
      setTemplates((tpls as Template[]) || []);
      const map: Record<string, AnalyticsRow> = {};
      if ((analyticsRes as any)?.success) {
        for (const r of ((analyticsRes as any).rows || []) as AnalyticsRow[]) {
          map[r.template_key] = r;
        }
      }
      setAnalytics(map);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const selected = useMemo(
    () => templates.find(t => t.template_key === selectedKey) || null,
    [templates, selectedKey],
  );

  const usedVars = useMemo(() => {
    if (!draft) return [];
    const found = new Set<string>();
    const re = /\{\{\s*(\w+)\s*\}\}/g;
    let m;
    const all = draft.subject + '\n' + draft.html_content;
    while ((m = re.exec(all)) !== null) found.add(m[1]);
    return Array.from(found);
  }, [draft]);

  const openEditor = (t: Template) => {
    setSelectedKey(t.template_key);
    setDraft({ ...t });
    setEditing(false);
    setMsg(null);
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setMsg(null);
    try {
      const { error } = await supabase
        .from('email_templates')
        .update({
          subject:      draft.subject,
          description:  draft.description,
          html_content: draft.html_content,
          updated_at:   new Date().toISOString(),
        })
        .eq('template_key', draft.template_key);
      if (error) throw error;
      setMsg({ ok: true, text: 'Template saved.' });
      setEditing(false);
      load();
    } catch (err) {
      setMsg({ ok: false, text: (err as Error).message });
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!draft || !testEmail.includes('@')) return;
    setSendingTest(true);
    setMsg(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const rendered = buildPreviewHtml(substitute(draft.html_content, SAMPLE_VARS));
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-broadcast-email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({
            segment:  'all',
            subject:  substitute(draft.subject, SAMPLE_VARS),
            bodyHtml: rendered,
            testEmail,
          }),
        },
      );
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(payload?.error || `Send failed (${res.status})`);
      setMsg({ ok: true, text: `Test sent to ${testEmail}.` });
    } catch (err) {
      setMsg({ ok: false, text: (err as Error).message });
    } finally {
      setSendingTest(false);
    }
  };

  const renderedIframe = useMemo(() => {
    if (!draft) return '';
    return buildPreviewHtml(substitute(draft.html_content, SAMPLE_VARS));
  }, [draft]);

  // ─── LIST VIEW ────────────────────────────────────────────────────
  if (!selected) {
    return (
      <div className="max-w-5xl mx-auto">
        <div className="mb-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-400 mb-2">
            ▸ Email templates
          </div>
          <h2 className="text-2xl font-black text-white">10 canonical emails.</h2>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">
            Every automated email the platform sends. Edit body + subject, preview mobile / desktop, test-send to any address before shipping.
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-500 font-mono text-xs">
            <FiLoader className="w-5 h-5 animate-spin mx-auto mb-2" />
            Loading templates…
          </div>
        ) : (
          <div className="space-y-2">
            {templates.map(t => (
              <button
                key={t.template_key}
                onClick={() => openEditor(t)}
                className="w-full text-left rounded-2xl border border-white/10 bg-black/40 p-4 hover:border-cyan-400/40 transition-colors"
              >
                <div className="flex items-center gap-3 flex-wrap">
                  <FiMail className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                  <span className="text-white font-bold">{t.template_key}</span>
                  <StatusPill ok={!!t.html_content} />
                  <span className="ml-auto text-[10px] font-mono text-gray-500 uppercase tracking-widest">
                    Updated {t.updated_at ? new Date(t.updated_at).toLocaleDateString() : '—'}
                  </span>
                </div>
                <div className="mt-2 text-sm text-gray-300 truncate">
                  {t.subject || <span className="text-gray-600 italic">no subject</span>}
                </div>
                {t.description && (
                  <div className="mt-1 text-xs text-gray-500 truncate">{t.description}</div>
                )}

                {/* Analytics strip — 30-day rolling window */}
                {analytics[t.template_key] && analytics[t.template_key].sent > 0 && (
                  <div className="mt-3 pt-3 border-t border-white/5 grid grid-cols-4 gap-3 text-xs font-mono">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-gray-500">Sent 30d</div>
                      <div className="text-white tabular-nums">{analytics[t.template_key].sent}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-gray-500">Delivered</div>
                      <div className="text-white tabular-nums">{analytics[t.template_key].delivered}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-cyan-400/70">Open rate</div>
                      <div className="text-cyan-300 tabular-nums">{fmtPct(analytics[t.template_key].open_rate)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-emerald-400/70">Click rate</div>
                      <div className="text-emerald-300 tabular-nums">{fmtPct(analytics[t.template_key].click_rate)}</div>
                    </div>
                  </div>
                )}
              </button>
            ))}
            {templates.length === 0 && (
              <div className="text-center py-14 border border-dashed border-white/10 rounded-2xl">
                <FiMail className="w-8 h-8 text-gray-600 mx-auto mb-3" />
                <p className="text-white font-medium mb-1">No templates in DB yet</p>
                <p className="text-xs text-gray-500 font-mono">
                  Apply migration 20260722180000_email_templates_v3_refonte.sql to seed the 10 canonical templates.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // ─── EDITOR + PREVIEW ─────────────────────────────────────────────
  return (
    <div className="max-w-6xl mx-auto">
      {/* Toolbar */}
      <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
        <button
          onClick={() => { setSelectedKey(null); setDraft(null); setEditing(false); }}
          className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-gray-500 hover:text-white"
        >
          <FiChevronLeft className="w-3.5 h-3.5" />
          Back to list
        </button>
        <div className="flex items-center gap-2">
          {editing ? (
            <>
              <button
                onClick={() => { setEditing(false); setDraft(selected); }}
                className="px-3 py-1.5 rounded-lg border border-white/10 text-gray-300 text-xs hover:bg-white/5"
              >
                Discard
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-gradient-to-r from-cyan-400 to-purple-400 text-black text-xs font-bold hover:scale-[1.02] transition-transform disabled:opacity-40"
              >
                {saving ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiSave className="w-3.5 h-3.5" />}
                Save
              </button>
            </>
          ) : (
            <button
              onClick={() => setEditing(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-cyan-400/30 text-cyan-300 text-xs hover:bg-cyan-500/10"
            >
              <FiEdit3 className="w-3.5 h-3.5" />
              Edit
            </button>
          )}
        </div>
      </div>

      {/* Header */}
      <div className="mb-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-400 mb-2">
          ▸ {selected.template_key}
        </div>
        <h2 className="text-2xl font-black text-white truncate">
          {editing ? draft?.subject : selected.subject}
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          {selected.description || <span className="italic">no description</span>}
        </p>
      </div>

      {msg && (
        <div className={`mb-4 px-4 py-2.5 rounded-lg border font-mono text-xs ${
          msg.ok ? 'bg-green-500/5 border-green-500/30 text-green-300' : 'bg-red-500/5 border-red-500/30 text-red-300'
        }`}>
          {msg.ok ? '✓' : '✗'} {msg.text}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Editor */}
        <div className="rounded-2xl border border-white/10 bg-black/40 p-4 space-y-3">
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-1.5">
              Subject
            </label>
            <input
              type="text"
              value={draft?.subject || ''}
              onChange={(e) => draft && setDraft({ ...draft, subject: e.target.value })}
              disabled={!editing}
              maxLength={200}
              className="w-full bg-black/60 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-cyan-400/50 disabled:opacity-70"
            />
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-1.5">
              Description (admin only)
            </label>
            <input
              type="text"
              value={draft?.description || ''}
              onChange={(e) => draft && setDraft({ ...draft, description: e.target.value })}
              disabled={!editing}
              className="w-full bg-black/60 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-cyan-400/50 disabled:opacity-70"
            />
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-1.5">
              Body HTML
            </label>
            <textarea
              value={draft?.html_content || ''}
              onChange={(e) => draft && setDraft({ ...draft, html_content: e.target.value })}
              disabled={!editing}
              rows={18}
              className="w-full bg-black/60 border border-white/10 rounded-lg px-3 py-2 text-white text-xs font-mono leading-relaxed focus:outline-none focus:border-cyan-400/50 disabled:opacity-70 resize-y"
            />
          </div>

          {usedVars.length > 0 && (
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">
                Variables in this template
              </div>
              <div className="flex flex-wrap gap-1.5">
                {usedVars.map((v) => (
                  <span key={v} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[10px] font-mono ${
                    SAMPLE_VARS[v]
                      ? 'bg-cyan-500/10 border-cyan-500/30 text-cyan-300'
                      : 'bg-amber-500/10 border-amber-500/30 text-amber-300'
                  }`}>
                    {`{{${v}}}`}
                    {!SAMPLE_VARS[v] && (
                      <span className="opacity-60">· unknown</span>
                    )}
                  </span>
                ))}
              </div>
              <p className="text-[10px] font-mono text-gray-600 mt-1.5">
                Cyan = has sample value used in preview. Amber = the Edge Function must supply this at send time.
              </p>
            </div>
          )}

          {/* Test send */}
          <div className="border-t border-white/5 pt-3">
            <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">
              Test send
            </div>
            <div className="flex gap-2">
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="you@example.com"
                className="flex-1 bg-black/60 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-cyan-400/50"
              />
              <button
                onClick={sendTest}
                disabled={!draft || !testEmail.includes('@') || sendingTest}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-cyan-400/30 text-cyan-300 hover:bg-cyan-500/10 text-xs disabled:opacity-40"
              >
                {sendingTest ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiSend className="w-3.5 h-3.5" />}
                Send test
              </button>
            </div>
            <p className="text-[10px] font-mono text-gray-600 mt-1.5">
              Uses sample values (Alex · $49.00 · Pro Trader …). Real sends use the Edge Function&apos;s live variables.
            </p>
          </div>
        </div>

        {/* Preview */}
        <div className="rounded-2xl border border-white/10 bg-black/40 p-4">
          <div className="mb-3 flex items-center justify-between gap-3 flex-wrap">
            <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">
              ▸ Preview
            </div>
            <div className="inline-flex rounded-lg border border-white/10 overflow-hidden">
              <button
                onClick={() => setPreviewMode('desktop')}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono uppercase tracking-widest ${
                  previewMode === 'desktop'
                    ? 'bg-cyan-500/10 text-cyan-300'
                    : 'text-gray-500 hover:text-gray-300'
                }`}
              >
                <FiMonitor className="w-3.5 h-3.5" />
                Desktop
              </button>
              <button
                onClick={() => setPreviewMode('mobile')}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono uppercase tracking-widest border-l border-white/10 ${
                  previewMode === 'mobile'
                    ? 'bg-cyan-500/10 text-cyan-300'
                    : 'text-gray-500 hover:text-gray-300'
                }`}
              >
                <FiSmartphone className="w-3.5 h-3.5" />
                Mobile
              </button>
            </div>
          </div>

          <div className="rounded-xl overflow-hidden bg-white flex justify-center">
            <div
              style={{
                width:  previewMode === 'mobile' ? 375 : '100%',
                height: 640,
                transition: 'width 250ms',
              }}
              className="max-w-full"
            >
              <iframe
                title="Email preview"
                srcDoc={renderedIframe}
                sandbox=""
                style={{ width: '100%', height: '100%', border: '0' }}
              />
            </div>
          </div>

          <p className="mt-2 text-[10px] font-mono text-gray-600">
            Rendered with the same wrapper the Edge Function ships. Tokens substituted with sample values.
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmailTemplatesPanel;
